import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';

import { OverridesDialog } from '@/components/team/OverridesDialog';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { canManageRole } from '@/lib/can-manage-role';
import { useHasPermission } from '@/lib/permissions';
import { ROLE_COLORS, ROLE_LABELS } from '@/lib/role-display';
import { MEMBER_STATUS_LABELS, MEMBER_STATUS_VARIANT, memberName, type Member } from '@/lib/users';
import { useAuth } from '@/store/auth';

interface Props {
  members: Member[] | undefined;
  isLoading?: boolean;
  onChangeRole: (m: Member) => void;
  onDelete: (m: Member) => void;
}

const lastLogin = (d: string | null) =>
  d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : 'Jamais';

export function MembersTable({ members, isLoading, onChangeRole, onDelete }: Props) {
  const me = useAuth((s) => s.session?.user);
  const canChangeRole = useHasPermission('users.changeRole');
  const canDelete = useHasPermission('users.delete');
  const [overridesTarget, setOverridesTarget] = useState<Member | null>(null);

  return (
    <div className="bg-card rounded-lg border">
      <OverridesDialog member={overridesTarget} onOpenChange={(o) => { if (!o) setOverridesTarget(null); }} />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nom</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Rôle</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead>Dernière connexion</TableHead>
            <TableHead className="w-12"><span className="sr-only">Actions</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && Array.from({ length: 4 }).map((_, i) => (
            <TableRow key={i}>{Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
          ))}
          {members?.map((m) => {
            // L'API refuse d'agir sur soi-même, et seul un OWNER touche à un OWNER (FINDING-016)
            const manageable = m.id !== me?.id && canManageRole(me?.role, m.role);
            const actions = manageable && (canChangeRole || canDelete);
            return (
              <TableRow key={m.membershipId}>
                <TableCell className="font-medium">{memberName(m)}</TableCell>
                <TableCell className="text-muted-foreground">{m.email}</TableCell>
                <TableCell><Badge className={ROLE_COLORS[m.role]}>{ROLE_LABELS[m.role]}</Badge></TableCell>
                <TableCell><Badge variant={MEMBER_STATUS_VARIANT[m.status]}>{MEMBER_STATUS_LABELS[m.status]}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">{lastLogin(m.lastLoginAt)}</TableCell>
                <TableCell>
                  {actions && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label={`Actions pour ${memberName(m)}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canChangeRole && (
                          <DropdownMenuItem onSelect={() => onChangeRole(m)}>Changer le rôle</DropdownMenuItem>
                        )}
                        <DropdownMenuItem disabled={!canChangeRole} onSelect={() => setOverridesTarget(m)}>
                          Permissions personnalisées
                        </DropdownMenuItem>
                        {canDelete && (
                          <DropdownMenuItem className="text-danger" onSelect={() => onDelete(m)}>Supprimer</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {members && members.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucun membre</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
